import { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { getAppById, TokoApp } from '@/config/apps';
import { ProgressCharts, MIN_SESSIONS_FOR_CHARTS } from '@/components/ProgressCharts';
import type { LessonSession } from '@/components/ProgressCharts';
import { calculateSessionStats } from '@/utils/sessionStats';
import { Pagination } from '@/components/Pagination';
import styles from './AppProgress.module.css';

const ITEMS_PER_PAGE = 10;

interface UnitProgress {
  schedule: string;
  current_unit: number;
  max_unit: number;
  last_updated: string;
}

export function AppProgress() {
  const { appId } = useParams<{ appId: string }>();
  const { user } = useAuth();
  const [sessions, setSessions] = useState<LessonSession[]>([]);
  const [unitProgress, setUnitProgress] = useState<UnitProgress[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const app: TokoApp | undefined = appId ? getAppById(appId) : undefined;

  useEffect(() => {
    if (!user || !appId) {
      setLoading(false);
      return;
    }

    const userId = user.id;

    async function fetchData() {
      const [sessionsResult, progressResult] = await Promise.all([
        supabase
          .from('lesson_sessions')
          .select('*')
          .eq('user_id', userId)
          .eq('app_name', appId)
          .order('completed_at', { ascending: false }),
        supabase
          .from('user_unit_progress')
          .select('*')
          .eq('user_id', userId)
          .eq('app_name', appId),
      ]);

      if (sessionsResult.error) {
        setError(sessionsResult.error.message);
      } else {
        setSessions(sessionsResult.data || []);
      }

      if (progressResult.error) {
        console.error('Error fetching unit progress:', progressResult.error);
      } else {
        setUnitProgress(progressResult.data || []);
      }

      setLoading(false);
    }

    fetchData();
  }, [user, appId]);

  const stats = useMemo(() => calculateSessionStats(sessions), [sessions]);

  // Calculate pagination for sessions
  const totalPages = Math.ceil(sessions.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedSessions = sessions.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  if (!app) {
    return (
      <div className={styles.container}>
        <Link to="/progress" className={styles.backLink}>Back to Progress</Link>
        <h1>App Not Found</h1>
        <p>The app "{appId}" does not exist.</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className={styles.container}>
        <Link to="/progress" className={styles.backLink}>Back to Progress</Link>
        <h1>{app.name} Progress</h1>
        <p>Please sign in to view progress.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={styles.container} style={{ '--app-color': app.color } as React.CSSProperties}>
        <Link to="/progress" className={styles.backLink}>Back to Progress</Link>
        <h1>{app.name} Progress</h1>
        <p>Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container} style={{ '--app-color': app.color } as React.CSSProperties}>
        <Link to="/progress" className={styles.backLink}>Back to Progress</Link>
        <h1>{app.name} Progress</h1>
        <p className={styles.error}>Error: {error}</p>
      </div>
    );
  }

  return (
    <div className={styles.container} style={{ '--app-color': app.color } as React.CSSProperties}>
      <Link to="/progress" className={styles.backLink}>Back to Progress</Link>

      <header className={styles.header}>
        <img src={app.icon} alt={app.name} className={styles.appIcon} />
        <div>
          <h1>{app.name}</h1>
          <p className={styles.subtitle}>{app.shortName} Progress</p>
        </div>
      </header>

      {sessions.length === 0 && (
        <div className={styles.appStoreBanner}>
          <img src={app.icon} alt={app.name} className={styles.bannerIcon} />
          <div className={styles.bannerContent}>
            <p className={styles.bannerTitle}>Haven't tried {app.name} yet?</p>
            <p className={styles.bannerSubtitle}>Start practicing math facts today!</p>
          </div>
          <Link to={app.appStoreUrl} className={styles.bannerLink}>
            Get on App Store
          </Link>
        </div>
      )}

      <section className={styles.section}>
        <h2>Schedule Progress</h2>
        <div className={styles.progressBars}>
          {app.schedules.map(schedule => {
            const progress = unitProgress.find(p => p.schedule === schedule.id);
            const currentUnit = progress?.current_unit ?? 0;
            const totalUnits = schedule.totalUnits;
            const hasStarted = currentUnit > 0;

            return (
              <div key={schedule.id} className={styles.progressItem}>
                <div className={styles.progressHeader}>
                  <span>{schedule.name}</span>
                  <span className={!hasStarted ? styles.notStarted : ''}>
                    {hasStarted ? `Unit ${currentUnit} / ${totalUnits}` : `0 / ${totalUnits} (Not started)`}
                  </span>
                </div>
                <div className={styles.progressBarContainer}>
                  <div
                    className={styles.progressBar}
                    style={{
                      width: `${(currentUnit / totalUnits) * 100}%`,
                      backgroundColor: hasStarted ? app.color : 'var(--color-border)',
                    }}
                  />
                </div>
                {schedule.description && (
                  <p className={styles.scheduleDescription}>{schedule.description}</p>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {stats ? (
        <>
          <section className={styles.section}>
            <h2>Overview</h2>
            <div className={styles.statsGrid}>
              <div className={styles.statCard}>
                <span className={styles.statValue}>{stats.totalSessions}</span>
                <span className={styles.statLabel}>Total Sessions</span>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statValue}>{stats.passRate.toFixed(0)}%</span>
                <span className={styles.statLabel}>Pass Rate</span>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statValue}>{stats.averageAccuracy.toFixed(1)}%</span>
                <span className={styles.statLabel}>Avg Accuracy</span>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statValue}>{stats.averageTime.toFixed(1)}s</span>
                <span className={styles.statLabel}>Avg Time/Card</span>
              </div>
            </div>
          </section>

          {sessions.length >= MIN_SESSIONS_FOR_CHARTS && (
            <section className={styles.section}>
              <h2>Statistics</h2>
              <ProgressCharts sessions={sessions} isLoading={loading} />
            </section>
          )}

          <section className={styles.section}>
            <h2>Recent Sessions</h2>
            <div className={styles.sessionsList}>
              {paginatedSessions.map(session => (
                <div key={session.id} className={styles.sessionCard}>
                  <div className={styles.sessionHeader}>
                    <span className={styles.sessionMode}>
                      {session.practice_mode === 'schedule'
                        ? `Schedule ${session.schedule} - Unit ${session.unit_number}`
                        : 'Free Play'}
                    </span>
                    <span className={`${styles.sessionStatus} ${session.passed ? styles.passed : styles.failed}`}>
                      {session.passed ? 'Passed' : 'Failed'}
                    </span>
                  </div>
                  <div className={styles.sessionStats}>
                    <span>Accuracy: {session.accuracy.toFixed(1)}%</span>
                    <span>Time: {(session.total_time / 1000).toFixed(1)}s / {session.goal_time}s</span>
                    <span>Correct: {session.correct_count}/{session.total_cards}</span>
                  </div>
                  <div className={styles.sessionDate}>
                    {new Date(session.completed_at).toLocaleDateString()} at{' '}
                    {new Date(session.completed_at).toLocaleTimeString()}
                  </div>
                </div>
              ))}
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </div>
          </section>
        </>
      ) : (
        <section className={styles.section}>
          <p className={styles.noData}>No sessions recorded yet for {app.name}.</p>
          <p>Start practicing to see your progress here!</p>
        </section>
      )}

      <section className={styles.section}>
        <h2>Raw Data</h2>
        <details>
          <summary>Session Data ({sessions.length} sessions)</summary>
          <pre className={styles.rawData}>
            {JSON.stringify(sessions, null, 2)}
          </pre>
        </details>
        <details>
          <summary>Unit Progress Data</summary>
          <pre className={styles.rawData}>
            {JSON.stringify(unitProgress, null, 2)}
          </pre>
        </details>
      </section>
    </div>
  );
}
